import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { updateBuildingGroupsTableConfig } from '../../../api'
import { BUILDING_GROUP_ENTITY } from '../../../constants/entity'
import { SUBMENU_DEFAULT_VALUE } from '../../../constants/route'
import { useReloadCountersAfterRefetch } from '../../../hooks'
import {
  BuildingGroupTableLayout,
  getBuildingGroupTableMassActions,
  useBuildingGroupTableActions,
  useBuildingGroupTablePage,
} from '../../BuildingGroup'
import Button from '../../Button'
import { FILTER_BAR_FILTER_TRIGGER_VARIANT, FilterBar } from '../../Filter'
import { PageActions, PageLoader } from '../../Page'
import { getEmptyStateActions, PageEmptyState } from '../../PageEmptyState'
import { TablePagination } from '../../TablePagination'

import {
  QA_NAME_ENTITY_TAB_BUILDING_GROUPS_ADD_NEW,
  QA_NAME_ENTITY_TAB_BUILDING_GROUPS_EDIT,
} from './EntityTabBuildingGroups.constants'
import { getEntityTabBuildingGroupsExcludeFilters } from './EntityTabBuildingGroups.utils'

import * as styles from './EntityTabBuildingGroups.module.css'

import BuildingGroupsIcon from '../../../icons/buildings-group.svg'
import EditIcon from '../../../icons/edit.svg'
import PlusIcon from '../../../icons/plus.svg'

function EntityTabBuildingGroups({
  entity = BUILDING_GROUP_ENTITY,
  filterStateApi,
  isLocalFilterState,
  qaName = QA_NAME_ENTITY_TAB_BUILDING_GROUPS_ADD_NEW,
  scopeFilterKey,
  scopeId,
  shouldShowCreateAction = true,
  shouldShowEditAction = false,
  onApplyTableConfig = updateBuildingGroupsTableConfig,
  onCreate,
  onEditBuildingGroups,
  onEntityReload,
  onReloadCounters,
}) {
  const { t } = useTranslation()

  const [modal, setModal] = useState(null)

  const scopeFilters = scopeFilterKey ? { [scopeFilterKey]: [scopeId] } : null

  const tablePage = useBuildingGroupTablePage({
    asyncTableOptions: {
      extraFilters: scopeFilters,
      filterStateApi,
      isLocalFilterState,
      withoutSubMenu: true,
    },
    entity,
    extraSelectionResetDeps: [scopeId],
    isBrowserTabTitleEnabled: false,
    sub: SUBMENU_DEFAULT_VALUE,
    onApplyTableConfig,
  })

  const {
    availableFilters,
    excludeIds,
    filterValues,
    handleChangeShouldReloadFilters,
    handleCleanSelection,
    handleLoadInit,
    handleReload,
    handleReloadTableAfterMassAction,
    handleSetFilterValues,
    handleSetPagination,
    hasFilterChanged,
    isLoading,
    isRefetching,
    items,
    ordering,
    pageScrollWrapperRef,
    pagination,
    permissions,
    selectAllPages,
    selectedIds,
    shouldReloadFilters,
  } = tablePage

  function handleCloseModal() {
    setModal(null)
  }

  async function handleReloadAfterMassAction() {
    onEntityReload?.()

    return handleReloadTableAfterMassAction()
  }

  const {
    getItemActions,
    isPerformingMassAction,
    setItemAction,
    setMassAction,
  } = useBuildingGroupTableActions({
    excludeIds,
    filterValues: { ...filterValues, ...scopeFilters },
    pagination,
    scopeBuildingId: scopeId,
    selectAllPages,
    selectedIds,
    sub: SUBMENU_DEFAULT_VALUE,
    onCleanSelection: handleCleanSelection,
    onCloseModal: handleCloseModal,
    onReload: handleReloadAfterMassAction,
    onSetModal: setModal,
  })

  useReloadCountersAfterRefetch({ isRefetching, onReloadCounters })

  useEffect(() => {
    if (scopeId) {
      handleLoadInit()
    }
  }, [scopeId])

  async function handleReloadWithEntity() {
    onEntityReload?.()

    return handleReload()
  }

  function handleAddBuildingGroup() {
    onCreate?.({ onReloadList: handleReloadWithEntity })
  }

  function handleEditBuildingGroups() {
    onEditBuildingGroups?.({ onReloadList: handleReloadWithEntity })
  }

  const massActions = getBuildingGroupTableMassActions({
    permissions,
    t,
    withDeleteFromGroup: Boolean(scopeId),
  })

  // The create right comes with the list response — the building profile
  // payload has no can_add_building_groups flag.
  const shouldShowCreate = shouldShowCreateAction && permissions?.can_create

  const hasBuildingGroups = items?.items?.length > 0

  const hasTable = hasBuildingGroups || isLoading

  const shouldCollapseToolbar =
    !hasBuildingGroups && !hasFilterChanged && !isLoading

  const emptyStateActions = getEmptyStateActions({
    actionName: qaName,
    isLoading,
    qaName,
    shouldShowActions: shouldShowCreate && !hasFilterChanged,
    text: t('BuildingGroupTable.AddNewBtnKey'),
    onClick: handleAddBuildingGroup,
  })

  return (
    <div className={styles.EntityTabBuildingGroups} ref={pageScrollWrapperRef}>
      {isPerformingMassAction || isRefetching ? <PageLoader /> : null}
      {shouldCollapseToolbar ? null : (
        <div className={styles.toolbar}>
          <FilterBar
            responsive
            availableFilters={availableFilters}
            className={styles.filterBar}
            entity={entity}
            excludeFilters={getEntityTabBuildingGroupsExcludeFilters(
              scopeFilterKey
            )}
            filterTriggerVariant={
              FILTER_BAR_FILTER_TRIGGER_VARIANT.COMPACT_ICON
            }
            filterValues={filterValues}
            isSkeleton={isLoading}
            ordering={ordering}
            pagination={{ per_page: pagination?.pageSize }}
            setFilterValues={handleSetFilterValues}
            shouldReloadValues={shouldReloadFilters}
            sub={SUBMENU_DEFAULT_VALUE}
            onChangeShouldReloadValues={handleChangeShouldReloadFilters}
            onReload={handleReload}
          />
          <PageActions className={styles.actions} isLoading={isLoading}>
            {shouldShowEditAction ? (
              <Button.Text
                beforeIconProps={{ Icon: EditIcon }}
                isSkeleton={isLoading}
                qaName={QA_NAME_ENTITY_TAB_BUILDING_GROUPS_EDIT}
                text={t('Building.EditBuildingsGroupActionOptionKey')}
                onClick={handleEditBuildingGroups}
              />
            ) : null}
            {shouldShowCreate ? (
              <Button.Text
                beforeIconProps={{ Icon: PlusIcon }}
                isSkeleton={isLoading}
                qaName={qaName}
                text={t('BuildingGroupTable.AddNewBtnKey')}
                onClick={handleAddBuildingGroup}
              />
            ) : null}
          </PageActions>
        </div>
      )}
      <BuildingGroupTableLayout
        emptyState={
          <PageEmptyState
            EntityIcon={BuildingGroupsIcon}
            actions={emptyStateActions}
            className={styles.emptyPage}
            text={t('BuildingGroupTable.NoDataKey')}
          />
        }
        getBuildingGroupItemActions={getItemActions}
        hasTable={hasTable}
        massActions={massActions}
        modal={modal}
        setItemAction={setItemAction}
        setMassAction={setMassAction}
        tablePage={tablePage}
      />
      {hasTable ? (
        <TablePagination
          responsive
          className={styles.pagination}
          isSkeleton={isLoading}
          value={pagination}
          onChange={handleSetPagination}
        />
      ) : null}
    </div>
  )
}

export default EntityTabBuildingGroups
